import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { JSX, ReactNode } from 'react';
import type { PageState, ServerMessage } from '@roll1/shared';
import type { Camera } from '../../types';
import { useSessionContext } from '../../contexts/SessionContext';
import {
  applyEchoToStoredPage,
  applySharedPageEcho,
  applyTextEchoToStoredPage,
  canvasEchoPageId,
} from '../../lib/canvas-page-echo';

/**
 * GM Studio map panes (package 5.3) — the Studio-level owner of what two live
 * maps side by side share.
 *
 * Each `StudioMapCanvas` is a complete working map of ONE page (own engine,
 * camera, content hooks, selection, undo, tool — see StudioMapCanvas). What
 * is NOT per pane lives here, once per Studio:
 *
 *  - the ONE echo router for page content. A page-content echo goes to the
 *    live hooks of the pane that holds its page, else to the page's store
 *    copy — never both, never twice (lib/canvas-page-echo). Per-pane routers
 *    would each apply the "not mine → store" branch;
 *  - the working map: the pane of the last pointer-down / focus. Canvas
 *    hotkeys act there only (MapPaneContext.ownsKeyboard). On mount the first
 *    pane works; when the working pane goes, the remaining one takes over;
 *  - pan/zoom per page for the life of Studio: a pane remounts on tab switch,
 *    ⤢ full / return and when the other pane closes, and comes back where the
 *    GM left it.
 */

/** What the router needs from a pane that holds a page. */
export interface MapPaneHolder {
  readonly pageId: string;
  /** A page-content echo of this page → the pane's live hooks. */
  apply(msg: ServerMessage, page: PageState | null): void;
  /** Hand the pane's live content back to the store. */
  saveToStore(): void;
  /** Reload the pane's hooks from this page state; pan/zoom stay. */
  reload(page: PageState): void;
}

export interface StudioMapPanesValue {
  /** Hold a page: its echoes go to this pane until the release. */
  register(holder: MapPaneHolder): () => void;
  /** Page of the working map (null: no map pane). */
  readonly workingPageId: string | null;
  /** Latest value — for key handlers between renders. */
  isWorking(pageId: string): boolean;
  setWorking(pageId: string): void;
  /** How many map panes are mounted. */
  readonly paneCount: number;
  rememberCamera(pageId: string, camera: Camera): void;
  recallCamera(pageId: string): Camera | undefined;
}

const StudioMapPanesContext = createContext<StudioMapPanesValue | null>(null);

export function useStudioMapPanes(): StudioMapPanesValue {
  const ctx = useContext(StudioMapPanesContext);
  if (ctx === null) throw new Error('useStudioMapPanes must be used within StudioMapPanesProvider');
  return ctx;
}

/** The same without throwing: a part that may stand outside the provider (the rail in its own tests). */
export function useOptionalStudioMapPanes(): StudioMapPanesValue | null {
  return useContext(StudioMapPanesContext);
}

export function StudioMapPanesProvider({ children }: { children: ReactNode }): JSX.Element {
  const { mp, pages } = useSessionContext();
  const pagesRef = useRef(pages);
  pagesRef.current = pages;

  const holdersRef = useRef(new Map<string, MapPaneHolder>());
  const [panes, setPanes] = useState<readonly string[]>([]);
  const workingRef = useRef<string | null>(null);
  const [workingPageId, setWorkingPageId] = useState<string | null>(null);
  const camerasRef = useRef(new Map<string, Camera>());

  const setWorking = useCallback((pageId: string | null): void => {
    if (workingRef.current === pageId) return;
    workingRef.current = pageId;
    setWorkingPageId(pageId);
  }, []);

  const register = useCallback((holder: MapPaneHolder): (() => void) => {
    holdersRef.current.set(holder.pageId, holder);
    setPanes((prev) => (prev.includes(holder.pageId) ? prev : [...prev, holder.pageId]));
    if (workingRef.current === null || !holdersRef.current.has(workingRef.current)) setWorking(holder.pageId);
    return () => {
      if (holdersRef.current.get(holder.pageId) !== holder) return;
      holdersRef.current.delete(holder.pageId);
      setPanes((prev) => prev.filter((id) => id !== holder.pageId));
      if (workingRef.current === holder.pageId) {
        const next = holdersRef.current.keys().next();
        setWorking(next.done === true ? null : next.value);
      }
    };
  }, [setWorking]);

  const isWorking = useCallback((pageId: string): boolean => workingRef.current === pageId, []);
  const setWorkingPane = useCallback((pageId: string): void => {
    if (holdersRef.current.has(pageId)) setWorking(pageId);
  }, [setWorking]);
  const rememberCamera = useCallback((pageId: string, camera: Camera): void => {
    camerasRef.current.set(pageId, camera);
  }, []);
  const recallCamera = useCallback((pageId: string): Camera | undefined => camerasRef.current.get(pageId), []);

  // The one Studio router for page content (the table's is useMultiplayerSync,
  // unmounted while Studio is shown). Campaign stores are routed above the
  // fork (useSessionStoreSync); this one only places page content.
  useEffect(() => {
    if (!mp.session) return;
    return mp.onMessage((msg: ServerMessage) => {
      const store = pagesRef.current;
      const holders = holdersRef.current;
      applySharedPageEcho(store, msg);
      const echoPageId = canvasEchoPageId(msg);
      if (echoPageId !== null) {
        const holder = holders.get(echoPageId);
        if (holder !== undefined) {
          holder.apply(msg, store.getPageData(echoPageId));
        } else {
          const pd = store.getPageData(echoPageId);
          if (pd) applyEchoToStoredPage(pd, msg);
        }
        return;
      }
      switch (msg.type) {
        // A pane's text hooks follow their own page (TextToolManager); the
        // store copy follows every page, so a remounted pane reloads it fresh.
        case 'text:created':
        case 'text:updated':
        case 'text:deleted':
        case 'text:moved': {
          const pd = store.getPageData(msg.pageId);
          if (pd) applyTextEchoToStoredPage(pd, msg);
          break;
        }
        case 'page:tokens-transferred': {
          const held = [msg.sourcePageId, msg.targetPageId]
            .map((id) => holders.get(id))
            .filter((h): h is MapPaneHolder => h !== undefined);
          for (const h of held) h.saveToStore();
          store.handleTokensTransferred(msg.sourcePageId, msg.targetPageId, msg.tokenIds, msg.tokens);
          for (const h of held) {
            const pd = store.getPageData(h.pageId);
            if (pd) h.reload(pd);
          }
          break;
        }
        // ADR-008 reconcile: the store took the fresh pages (useSessionStoreSync);
        // a pane holding one reloads it — its hooks are older than the batch.
        case 'state:batch-update':
          for (const pid of msg.affected.pages) {
            const holder = holders.get(pid);
            const fresh = msg.state.pages.find((p) => p.id === pid);
            if (holder !== undefined && fresh !== undefined) holder.reload(fresh);
          }
          break;
        default:
          break;
      }
    });
  }, [mp]);

  const value = useMemo<StudioMapPanesValue>(() => ({
    register,
    workingPageId,
    isWorking,
    setWorking: setWorkingPane,
    paneCount: panes.length,
    rememberCamera,
    recallCamera,
  }), [register, workingPageId, isWorking, setWorkingPane, panes.length, rememberCamera, recallCamera]);

  return <StudioMapPanesContext.Provider value={value}>{children}</StudioMapPanesContext.Provider>;
}
